import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '../i18n/config';
import { changeLanguage } from '../i18n';
import { buildPath, parsePath } from '../routing';
import { ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = SUPPORTED_LANGUAGES.find(
    (lang) => lang.code === i18n.language
  ) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code: LanguageCode) => {
    changeLanguage(code);
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      const { route } = parsePath(window.location.pathname);
      const hash = window.location.hash || '';
      window.location.href = `${buildPath(code, route)}${hash}`;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-100/50 text-gray-700 hover:bg-teal-200/50 transition-colors"
        type="button"
        aria-label={t('languageSwitcher.label')}
      >
        <span className="text-xl">{currentLang.flag}</span>
        <span className="font-medium">{currentLang.name}</span>
        <ChevronDown className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute rounded-lg border border-teal-200/50 overflow-hidden shadow-lg bg-white"
          style={{ top: '100%', left: 0, marginTop: '0.5rem', minWidth: '12rem' }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-colors ${
                lang.code === currentLang.code 
                  ? 'bg-teal-200 text-teal-700' 
                  : 'hover:bg-teal-100 text-gray-700'
              }`}
              type="button"
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
